// <pfs-shell> — generische Isolations-Boundary + Client-Router der
// PFS-Plattform (ADR-0012). KENNT KEIN LAYOUT.
//
// `<pfs-shell>` ist NUR Mechanik: Shadow-Boundary (P1) + Client-Router
// (P2). Es weiß NICHTS über Sidebar, Tabs, Kategorien, Slots, Branding
// oder ein Manifest — das ist alles Sache des jeweiligen
// Per-Environment-Aggregators (z.B. mednet-main), der seine KOMPLETTE
// Shell server-seitig in die Declarative-Shadow-Root rendert.
//
// Grund (ADR-0008 §0.1 / Pascal 2026-05-18): "nicht jede App ist auf
// eine Sidebar aufgebaut — jede Umgebung hat ihre eigene UI". Ein
// echtes `<slot>` (Light-DOM-Projektion) gäbe KEINE CSS-Isolation
// (slotted content wird vom Host-Dokument gestylt); daher muss der
// Konsumenten-Inhalt SHADOW-INTERN liegen → Declarative Shadow DOM.
//
// Konsumenten-Contract (alles vom Aggregator in seinem SSR-Markup):
//
//   <pfs-shell>
//     <template shadowrootmode="open">
//       <link rel="stylesheet" href="<prefix>/static/css/styles.css">
//       …komplette Aggregator-Shell (Sidebar/Nav/Tabs/Slots/…)…
//       <a data-pfs-route href="<prefix>/user">Benutzer</a>
//       <main data-pfs-content> …Route-Inhalt (SSR) … </main>
//     </template>
//   </pfs-shell>
//
//   data-pfs-route   = Routen-Trigger. Klick → preventDefault,
//                       history.pushState(Top-Dok, href), Partial-Fetch,
//                       Swap der Region(en), Active-Toggle.
//   data-pfs-content = Default-Swap-Region (Legacy/Single-Region). Der
//                       Router ersetzt deren innerHTML mit der ganzen
//                       Partial-Response, WENN diese keine benannten
//                       Regionen traegt (backward-compat).
//   data-pfs-region  = Benannte Swap-Region (Multi-Region, PFS-461). Die
//                       Partial-Response kann ein ODER mehrere
//                       [data-pfs-region="<name>"]-Fragmente tragen; der
//                       Router swap't jedes in die gleichnamige Live-Region
//                       (innerHTML). Regionen, die in der Response FEHLEN,
//                       bleiben unangetastet. So updatet ein Aggregator bei
//                       Sektionswechsel mehrere Zonen (z.B. "tabs" +
//                       "content") und bei Tab-Wechsel nur "content".
//   X-PFS-Partial: 1 = Request-Header den der Router mitschickt; der
//                       Aggregator liefert dann NUR das/die Region-Markup
//                       (kein Shell-Geruest).
//
// pfs-shell kennt KEINE Region-Namen — "tabs"/"content" sind reine
// Aggregator-Konvention. Der Router matched nur Live-Region ↔ Response-
// Region per data-pfs-region-Wert. Layout/Grid/Borders gehoeren komplett
// dem Aggregator (mednet-main), nicht dieser Lib.
//
// Initial-Load/Deep-Link: der Aggregator hat den korrekten Inhalt für
// die aktuelle URL bereits server-seitig in die DSD gerendert → der
// Router fetcht NICHT beim Connect (kein Redundanz-Roundtrip), setzt
// nur den Active-State. Echte Navigation/popstate → Fetch+Swap.

import { defineComponent } from "../../composition/define-component.js";
import { withDeclarativeShadow } from "../../composition/with-declarative-shadow.js";
import type { ComponentContext } from "../../composition/define-component.js";
import { traceManager } from "../../utils/trace-manager.js";
import { escalateIfSessionDead } from "../../store/api-fetch.js";
import { withApiBase } from "../../store/api-fetch.js";
import { startSessionExpiryWatcher } from "../../store/session-watcher.js";

export const PFS_SHELL_ROUTE_ATTR = "data-pfs-route";
export const PFS_SHELL_CONTENT_ATTR = "data-pfs-content";
export const PFS_SHELL_REGION_ATTR = "data-pfs-region";
export const PFS_SHELL_PARTIAL_HEADER = "X-PFS-Partial";
// PFS-464: waehrend einer Navigation (Fetch+Swap) gesetzt am <pfs-shell>-
// Host. Konsumenten stylen rein per CSS via :host([data-pfs-navigating])
// (z.B. einen Loading-Indicator) — kein JS/Event noetig.
export const PFS_SHELL_NAV_ATTR = "data-pfs-navigating";
// PFS-122/PFS-330: per-Route deklarierte Query-Params, die beim Navigieren aus
// der aktuellen URL in die Ziel-URL uebernommen werden (space/komma-separiert).
// Generisch — die Cut-App deklariert sie im Manifest, mednet-main rendert sie an
// den Route-Anchor. So bleibt z.B. ?practice= ueber Tab/Route-Nav erhalten, ohne
// dass pfs-shell oder die Cut-App Domain-Wissen hardcoden.
export const PFS_SHELL_PRESERVE_ATTR = "data-pfs-preserve";

const ROUTE_SEL = `[${PFS_SHELL_ROUTE_ATTR}]`;
const CONTENT_SEL = `[${PFS_SHELL_CONTENT_ATTR}]`;
const REGION_SEL = `[${PFS_SHELL_REGION_ATTR}]`;

// hrefPath: absolute Pfad+Query eines Trigger-Elements. Bevorzugt
// href (Anchor), sonst der data-pfs-route-Wert. Gegen location.href
// aufgelöst → funktioniert mit beliebigem Prefix (standalone "" /
// embed "/member-area").
function hrefPath(el: Element): string | null {
  const raw = el.getAttribute("href") ?? el.getAttribute(PFS_SHELL_ROUTE_ATTR);
  if (!raw) return null;
  try {
    const u = new URL(raw, location.href);
    if (u.origin !== location.origin) return null; // nur same-origin intern
    return u.pathname + u.search;
  } catch {
    return null;
  }
}

// Param-Resolver-Registry (PFS-122/PFS-330): async Fallback-Funktion pro
// Query-Param-NAME. Greift, wenn ein in data-pfs-preserve deklarierter Param
// beim Navigieren NICHT aus der aktuellen URL uebernommen werden kann (z.B. nach
// Verlassen+Rueckkehr in einen Kontext). Arbeitsteilung: die POLICY (welche
// Params preserved werden) ist Manifest/Aggregator-owned (data-pfs-preserve);
// die Funktion liefert nur den DOMAENEN-WERT (z.B. practice aus sessionStorage)
// und wird vom Kontext-Owner (Cut-App) registriert. pfs-shell schreibt den
// zurueckgegebenen Wert in die Ziel-URL — URL-Writing bleibt im Router.
type ParamResolver = () => string | null | undefined | Promise<string | null | undefined>;
// Registry liegt auf einem globalThis-Global, damit sie die BUNDLE-Grenze
// ueberquert: Cut-Apps bundlen ihre EIGENE @pfs/components-Kopie (esbuild
// bundle:true), die laufende pfs-shell kommt aber aus dem Vendor-Bundle
// (mednet-main serviert pfs-components.js). Eine modul-level Map waere pro
// Bundle-Instanz getrennt → Registrierung (Cut-App) + Lookup (Vendor-Shell)
// verfehlen sich. Das Global teilen beide Instanzen.
declare global {
  // eslint-disable-next-line no-var
  var __pfsParamResolvers: Map<string, ParamResolver> | undefined;
}
function resolverRegistry(): Map<string, ParamResolver> {
  return (globalThis.__pfsParamResolvers ??= new Map<string, ParamResolver>());
}
export function registerParamResolver(name: string, resolver: ParamResolver): void {
  resolverRegistry().set(name, resolver);
}

// applyPreservedParams: fuer jeden in data-pfs-preserve gelisteten Param —
// (Prio 1) aus der AKTUELLEN URL uebernehmen, sofern der Link ihn nicht selbst
// setzt; sonst (Prio 2) den registrierten Resolver fragen. Async (Resolver
// duerfen async sein). Per-Route by construction (Liste am Anchor).
async function applyPreservedParams(path: string, el: Element): Promise<string> {
  const raw = el.getAttribute(PFS_SHELL_PRESERVE_ATTR);
  if (!raw) return path;
  const names = raw.split(/[\s,]+/).filter(Boolean);
  if (names.length === 0) return path;
  let u: URL;
  try {
    u = new URL(path, location.href);
  } catch {
    return path;
  }
  const current = new URLSearchParams(location.search);
  for (const name of names) {
    if (u.searchParams.has(name)) continue; // Link gewinnt
    const cur = current.get(name);
    if (cur !== null) {
      u.searchParams.set(name, cur); // Prio 1: aktuelle URL
      continue;
    }
    const resolver = resolverRegistry().get(name);
    if (resolver) {
      try {
        const val = await resolver(); // Prio 2: Kontext-Owner-Resolver
        if (val) u.searchParams.set(name, val);
      } catch {
        /* Resolver-Fehler ignorieren — Param bleibt weg */
      }
    }
  }
  return u.pathname + u.search;
}

// syncParamToUrl (PFS-330): schreibt `?name=value` in die aktuelle URL via
// replaceState — aber NUR wenn die AKTIVE Route den Param via data-pfs-preserve
// deklariert (route-aware). Der Router besitzt Routen + URL + aktive Route;
// Consumer (z.B. mednet-mains Context-Owner) rufen DIESE API, statt selbst in die
// Shell-Shadow zu greifen + replaceState zu machen. `value=null` entfernt den
// Param. No-op, wenn keine aktive Route den Param repraesentiert (z.B. /user).
export function syncParamToUrl(name: string, value: string | null): void {
  if (typeof document === "undefined") return;
  const root: Document | ShadowRoot =
    document.querySelector("pfs-shell")?.shadowRoot ?? document;
  const active = root.querySelectorAll(`${ROUTE_SEL}[aria-current="page"]`);
  let represents = false;
  for (const el of Array.from(active)) {
    const names = (el.getAttribute(PFS_SHELL_PRESERVE_ATTR) ?? "")
      .split(/[\s,]+/)
      .filter(Boolean);
    if (names.includes(name)) {
      represents = true;
      break;
    }
  }
  if (!represents) return;
  const u = new URL(location.href);
  if (value) u.searchParams.set(name, value);
  else u.searchParams.delete(name);
  const target = u.pathname + u.search;
  if (target !== location.pathname + location.search) {
    history.replaceState(history.state, "", target);
  }
}

defineComponent({
  tagName: "pfs-shell",
  mixins: [withDeclarativeShadow()],
  render(host: HTMLElement, ctx: ComponentContext) {
    const root = ctx.shadowRoot;
    if (!root) return;

    // PFS-Embed-Flow: die Shell ist ein Flex-Column-FILL-Container — ihr
    // direkter Route-Content waechst, um die (vom Konsumenten/Host gesetzte)
    // Shell-Hoehe zu fuellen. Damit loest sich `h-full`/Zentrierung im
    // injizierten Content auf, OHNE dass jede Konsumenten-Route die
    // verschachtelte Hoehen-Kette selbst aufloesen muss — und identisch in
    // standalone (Host = Viewport-hoch) wie embed (Host = flex-gefuellt).
    // KEIN Layout/Grid (das bleibt beim Konsumenten) — nur Fill-Sizing.
    // `:where()` haelt die Spezifitaet bei 0, Konsumenten-Regeln gewinnen.
    if (!host.hasAttribute("data-pfs-fill-applied")) {
      const fill = new CSSStyleSheet();
      fill.replaceSync(
        // PFS-580: `position:relative` macht die Shell zum Positioning-Context
        // der App-Root-Floating-Ebene. Floating-WCs (toast/banner/overlay), die
        // als `position:absolute` IN der Shell-Shadow sitzen, scopen damit auf die
        // Shell-Box — standalone = Viewport-gross, embed = nur die Embed-Region.
        // Loest den `fixed`→Viewport-Escape im same-document WP-Embed.
        ":host{display:flex;flex-direction:column;position:relative;}" +
          ":host > :where(:not(link):not(style):not(script):not(template)){flex:1 0 auto;min-height:0;}",
      );
      root.adoptedStyleSheets = [...root.adoptedStyleSheets, fill];
      host.setAttribute("data-pfs-fill-applied", "");
    }

    const setActive = (pathname: string): void => {
      // Items inside a swap region — a named [data-pfs-region] OR the legacy
      // default [data-pfs-content] — get their aria-current from the server
      // (SSR initial render AND every partial-swap response carry it). Skip
      // them so the client router never fights the server-set tab/content
      // state. Only items OUTSIDE any swap region (the persistent sidebar /
      // mobile drawer) are managed here via prefix-match.
      const regions = [
        ...root.querySelectorAll(REGION_SEL),
        ...root.querySelectorAll(CONTENT_SEL),
      ];
      const insideRegion = (el: Element): boolean =>
        regions.some((r) => r.contains(el));
      root.querySelectorAll(ROUTE_SEL).forEach((el) => {
        if (insideRegion(el)) return;
        const elHref = hrefPath(el);
        const active =
          elHref !== null &&
          (elHref === pathname ||
            (!elHref.includes("?") && pathname.startsWith(elHref + "/")) ||
            (!elHref.includes("?") && pathname.startsWith(elHref + "?")));
        if (active) el.setAttribute("aria-current", "page");
        else el.removeAttribute("aria-current");
      });
    };

    // applyPartial — Multi-Region-Swap (PFS-461). Traegt die Response ein
    // oder mehrere [data-pfs-region="<name>"]-Fragmente, swap't jedes in die
    // gleichnamige Live-Region (innerHTML); in der Response fehlende Regionen
    // bleiben unangetastet. Traegt sie KEINE Region-Marker, gilt der Legacy-
    // Pfad: ganze Response → [data-pfs-content].innerHTML (Single-Region,
    // backward-compat fuer noch nicht migrierte Konsumenten wie demo-main).
    const applyPartial = (html: string): void => {
      const tpl = document.createElement("template");
      tpl.innerHTML = html;
      const incoming = tpl.content.querySelectorAll(REGION_SEL);
      if (incoming.length === 0) {
        const target = root.querySelector(CONTENT_SEL);
        if (target) target.innerHTML = html;
        return;
      }
      const live = new Map<string, Element>();
      root.querySelectorAll(REGION_SEL).forEach((el) => {
        const name = el.getAttribute(PFS_SHELL_REGION_ATTR);
        if (name && !live.has(name)) live.set(name, el);
      });
      incoming.forEach((frag) => {
        const name = frag.getAttribute(PFS_SHELL_REGION_ATTR);
        if (!name) return;
        const target = live.get(name);
        if (target) target.innerHTML = frag.innerHTML;
      });
    };

    const swap = async (rawPath: string, el: Element | null, push: boolean): Promise<void> => {
      // Kein Swap-Target (weder benannte Region noch Legacy-Default) → no-op.
      if (!root.querySelector(REGION_SEL) && !root.querySelector(CONTENT_SEL)) {
        return;
      }
      // PFS-122/PFS-330: deklarierte Kontext-Query-Params (data-pfs-preserve am
      // geklickten Link) erhalten/per Resolver wiederherstellen. popstate (el=
      // null) braucht das nicht — die URL traegt die Params bereits.
      const pathname = el ? await applyPreservedParams(rawPath, el) : rawPath;
      // PFS-464: Loading-State am Host fuer die Dauer des Fetch+Swap.
      // finally → auch bei !res.ok und Netzwerk-Fehler wieder entfernt.
      host.setAttribute(PFS_SHELL_NAV_ATTR, "");
      try {
        let html: string;
        try {
          // PFS-440: Partial-Fragment fetch ist HTML, kein JSON → apiFetch
          // passt nicht (JSON-only). Manueller X-TRACE-ID-Header aus
          // traceManager damit der Server-Side Access-Log + Inbound-
          // Middleware die Browser-Session-Sticky-ID sieht.
          const res = await fetch(withApiBase(pathname), {
            headers: {
              [PFS_SHELL_PARTIAL_HEADER]: "1",
              "X-TRACE-ID": traceManager.getTraceId(),
            },
            credentials: "include",
          });
          // PFS-467-Lücke: dieser Roh-fetch (HTML-Fragment, kein apiFetch) hat den
          // 401→Session-Expired-Flow bisher verschluckt → Nav-Klick bei abgelaufenem
          // Token = stilles No-op. Jetzt: bei 401 zentral eskalieren (Verifier-Probe
          // → „Sitzung abgelaufen"-Banner + Auto-Logout, nur bei real toter Session).
          if (res.status === 401) {
            await escalateIfSessionDead();
            return;
          }
          if (!res.ok) return;
          html = await res.text();
        } catch {
          return;
        }
        applyPartial(html);
        if (push && location.pathname + location.search !== pathname) {
          history.pushState({ pfsShell: true }, "", pathname);
        }
        setActive(pathname);
      } finally {
        host.removeAttribute(PFS_SHELL_NAV_ATTR);
      }
    };

    const onClick = (e: Event): void => {
      let el: Element | null = e.target as Element | null;
      while (el && el !== (root as Node)) {
        if (el.hasAttribute?.(PFS_SHELL_ROUTE_ATTR)) break;
        el = el.parentElement;
      }
      if (!el || !el.hasAttribute?.(PFS_SHELL_ROUTE_ATTR)) return;
      const rawPath = hrefPath(el);
      if (rawPath === null) return; // extern/ungültig → Browser-Default
      const me = e as MouseEvent;
      if (me.metaKey || me.ctrlKey || me.shiftKey || me.button > 0) return; // neue Tabs etc.
      e.preventDefault();
      void swap(rawPath, el, true);
    };

    const onPop = (): void => {
      void swap(location.pathname + location.search, null, false);
    };

    root.addEventListener("click", onClick);
    window.addEventListener("popstate", onPop);
    ctx.cleanup.push(() => root.removeEventListener("click", onClick));
    ctx.cleanup.push(() => window.removeEventListener("popstate", onPop));

    // Lever B: proaktiver Session-Expiry-Timer. Feuert „Sitzung abgelaufen" auch
    // ohne Interaktion (Idle) — interaktions-unabhängig, ergänzt den reaktiven
    // 401-Pfad (Lever A). Genau ein Start pro Connect; Stop via cleanup.
    ctx.cleanup.push(startSessionExpiryWatcher());

    // Initial: KEIN Fetch (SSR-DSD ist bereits korrekt für die aktuelle
    // URL) — nur Active-State setzen.
    setActive(location.pathname + location.search);
  },
});
